import { cva, cx } from '@vizit/theme';
import { hasScore } from '@vizit/utils';
import React from 'react';
import {
  type AriaButtonOptions,
  type PressEvent,
  mergeProps,
  useButton,
  useFocusRing,
  useFocusWithin,
  useHover,
} from 'react-aria';
import { AssetContext as AssetContextValue } from './asset.context-provider';
import { BUTTON_TOP_INSET, LABELED_BUTTON_TOP_OFFSET } from './constants';
import type { AssetProps } from './types';

export const styles = cva({
  base: 'relative outline-none transition-all ease-in-out',
  variants: {
    isHovered: {
      // TODO: There is currently no hover state at this level, but this is a placeholder for when we add it
      true: '',
      false: '',
    },
    isDisabled: {
      true: 'cursor-wait',
      false: '',
    },
    isViewOnly: {
      true: 'cursor-default',
      false: 'cursor-pointer',
    },
  },
  defaultVariants: {
    isViewOnly: false,
  },
});

function Asset(props: AssetProps) {
  const {
    children,
    className,
    id,
    isLoading = false,
    isSelected = false,
    isUnscored = false,
    unscoredLabel,
    isAiGenerated = false,
    generatedChildCount,
    isViewOnly = false,
    score,
    showScoreBar = true,
    slotNumber = null,
    buttonTopOffset,
    onPress,
    onAssetClick,
    onHoverStart,
    onHoverEnd,
    onHoverChange,
    ...other
  } = props;

  const ref = React.useRef<HTMLDivElement>(null);

  const [isHovered, setIsHovered] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);

  const [hasContextMenu, setHasContextMenu] = React.useState(false);
  const [hasLabel, setHasLabel] = React.useState(false);
  const isScored = React.useMemo(() => hasScore(score), [score]);

  // The hover action buttons are positioned against this root, which starts at
  // the label when one is rendered. Without the label's height they would sit
  // above the image instead of inside it.
  const resolvedButtonTopOffset = buttonTopOffset ?? (hasLabel ? LABELED_BUTTON_TOP_OFFSET : BUTTON_TOP_INSET);

  const handlePress = React.useCallback(
    (event: PressEvent) => {
      onAssetClick?.(id);
      onPress?.(event);
    },
    [id, onAssetClick, onPress],
  );

  // The tile's own hover state feeds its hover-only chrome; the caller's hover
  // events ride along so a surface can react to the pointer (e.g. previewing
  // the hovered image) without re-implementing pointer tracking.
  const handleHoverChange = React.useCallback(
    (isHovering: boolean) => {
      setIsHovered(isHovering);
      onHoverChange?.(isHovering);
    },
    [onHoverChange],
  );
  const { hoverProps } = useHover({ onHoverStart, onHoverEnd, onHoverChange: handleHoverChange });
  const { focusWithinProps } = useFocusWithin({ onFocusWithinChange: setIsFocused });
  // Focus-within reveals the hover actions, but only keyboard (focus-visible)
  // focus may paint the tile's selected ring: a mouse click on an action
  // button leaves that button focused, and ringing the tile then reads as a
  // second selection.
  const { isFocusVisible, focusProps: focusRingProps } = useFocusRing({ within: true });
  const { buttonProps } = useButton(
    {
      ...(other as AriaButtonOptions<'div'>),
      onPress: handlePress,
      elementType: 'div',
      isDisabled: isLoading,
    },
    ref,
  );

  const interactionProps = isViewOnly
    ? mergeProps(focusWithinProps, focusRingProps)
    : mergeProps(buttonProps, hoverProps, focusWithinProps, focusRingProps);

  return (
    <AssetContextValue.Provider
      value={{
        hasContextMenu,
        isHovered,
        isFocused,
        isFocusVisible,
        isLoading,
        isSelected: isSelected || isFocusVisible,
        isUnscored,
        unscoredLabel,
        isAiGenerated,
        generatedChildCount,
        isViewOnly,
        score: isScored ? score : null,
        showScoreBar,
        slotNumber,
        buttonTopOffset: resolvedButtonTopOffset,
        onContextMenuMount: setHasContextMenu,
        onLabelMount: setHasLabel,
        onHoverChange: setIsHovered,
      }}
    >
      <div
        className={cx(styles({ isHovered, isDisabled: isLoading, isViewOnly }), className)}
        {...interactionProps}
        data-focus={isFocused}
        data-focus-visible={isFocusVisible}
        data-hovered={isHovered}
        {...other}
      >
        {children}
      </div>
    </AssetContextValue.Provider>
  );
}

export { Asset };
